import {FC, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import {Navigate, useLocation, useNavigate, useParams} from 'react-router-dom'
import {AxiosError} from 'axios'
import clsx from 'clsx'
import {isNotEmpty, QUERIES} from '../../../../../../_metronic/helpers'
import {AppLoading} from '../../../../../../_metronic/helpers/components/AppLoading'
import {getInvoiceByHashCode} from '../core/_requests'
import {InvoicePrintModalPrint} from './InvoicePrintModalPrint'
import {InvoicePrintPOS} from './InvoicePrintPOS'
import {PrintFooter} from './PrintFooter'
import {useAuth} from '../../../../auth'
import {
  getInvoicePrintPageForLink,
  newPrintPageUrl,
} from '../../invoice-print/core/printPagePreference'
import {getInvoicePrint, InvoicePrintRequestError} from '../../invoice-print/core/_requests'
import {InvoicePrintModel} from '../../invoice-print/core/_models'
import {printLayoutOf} from '../../invoice-print/core/printLayoutSettings'
import {noteLines as printedNoteLines} from '../../invoice-print/core/printModel'
import {classicSubtitle, classicTitle} from './classic/classicPrintModel'
import {
  PaymentResultBanner,
  readPaymentParam,
} from '../../../../online-payment/components/PaymentResultBanner'
import './InvoicePrintModal.css'

/** The text the server's PDF renderer looks for in the printed PDF before it keeps the file (backend 1.0.0.687 and later; it also still accepts MEGAPAY_INVOICE_PDF_READY). */
export const PDF_READY_TEXT = 'ASATSP_INVOICE_PDF_READY'

const invoiceErrorMessage = (error: unknown) => {
  const response = (error as AxiosError)?.response
  if (!response) {
    return 'اتصال به سامانه برقرار نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.'
  }
  if (response.status === 404) {
    return 'صورتحسابی با این لینک پیدا نشد. لینک را کامل و بدون تغییر باز کنید.'
  }
  if (response.status === 403) return 'اجازهٔ دیدن این صورتحساب را ندارید.'
  return 'دریافت صورتحساب انجام نشد. چند لحظه دیگر دوباره تلاش کنید.'
}

const ErrorState: FC<{message: string; onRetry: () => void}> = ({message, onRetry}) => (
  <div className='cp-state' role='alert' dir='rtl'>
    <div className='cp-state__card'>
      <h1 className='cp-state__title'>صورتحساب دریافت نشد</h1>
      <p className='cp-state__text'>{message}</p>
      <button type='button' className='btn btn-sm btn-primary' onClick={onRetry}>
        تلاش مجدد
      </button>
    </div>
  </div>
)

const InvoicePrintModalPrintWrapper = () => {
  const {hash} = useParams()
  const {search} = useLocation()
  const {currentUser} = useAuth()
  const pdfRenderToken = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('pdfToken')
  // Links to this address (messages, API answers, older papers' QR codes) open the print page the
  // invoice's business chose. This page itself opens when the server renders its PDF, or when it
  // is asked for (?page=classic, from the invoice menu) by a signed-in user: without signing in it
  // shows nothing, so a visitor always gets the new page, which opens for anyone.
  const signedIn = Boolean(currentUser)
  const classicRequested = new URLSearchParams(search).get('page') === 'classic'
  const followsBusiness = !pdfRenderToken && signedIn && !classicRequested
  const businessPage = useQuery(
    ['invoice-print-page', hash],
    () => getInvoicePrintPageForLink(hash || ''),
    {
      enabled: followsBusiness && isNotEmpty(hash),
      cacheTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
    }
  )
  const showsThisPage =
    Boolean(pdfRenderToken) ||
    (signedIn && (classicRequested || businessPage.data === 'classic' || businessPage.isError))
  const enabledQuery: boolean = isNotEmpty(hash) && showsThisPage
  // One state for the screen and the paper: the sheets on screen are the ones printed.
  const [withSign, setWithSign] = useState<string | null>(null)
  const [fullPrint, setFullPrint] = useState(false)
  const [layoutReady, setLayoutReady] = useState(false)

  // The panel's invoice: payment details, attachments, the receipt on phones and the toolbar.
  const invoiceQuery = useQuery(
    `${QUERIES.INVOICES_LIST}-invoice-${hash}`,
    () => getInvoiceByHashCode(hash || '', pdfRenderToken || undefined),
    {cacheTime: 0, enabled: enabledQuery}
  )
  // The printed invoice: every value the sheets print, by the same rules as the A4 page. With the
  // renderer's token the server does not count the request against the visitor's rate.
  const modelQuery = useQuery<InvoicePrintModel, InvoicePrintRequestError>(
    ['classic-invoice-print', hash, Boolean(pdfRenderToken)],
    () => getInvoicePrint(hash || '', pdfRenderToken || undefined),
    {
      enabled: enabledQuery,
      cacheTime: 0,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: (count, error) =>
        (error?.kind === 'network' || error?.kind === 'unavailable') && count < 2,
    }
  )
  const invoice = invoiceQuery.data
  const model = modelQuery.data
  const receiptNotes = useMemo(() => (model ? printedNoteLines(model) : []), [model])
  const navigate = useNavigate()
  // Back from a gateway: the payment's result, until the payer closes it.
  const [paymentId, setPaymentId] = useState(() => (pdfRenderToken ? undefined : readPaymentParam()))

  if (hash && !pdfRenderToken) {
    if (!signedIn || businessPage.data === 'new') {
      // A payer back from the gateway keeps the result on the page they land on.
      return <Navigate to={newPrintPageUrl(hash) + (paymentId ? `?payment=${paymentId}` : '')} replace />
    }
    if (followsBusiness && !businessPage.data && !businessPage.isError) {
      return <AppLoading mode='page' message='در حال دریافت صورتحساب…' />
    }
  }

  if (invoice && model) {
    const effectiveWithSign = withSign ?? (printLayoutOf(model).printSignatureByDefault ? '1' : '0')
    return (
      <div className='invoice-print-wrapper cp-page' dir='rtl'>
        {layoutReady && (
          <span aria-hidden='true' className='invoice-pdf-ready-marker'>
            {PDF_READY_TEXT}
          </span>
        )}
        {paymentId && (
          <div className='cp-noprint px-3'>
            <PaymentResultBanner
              key={paymentId}
              publicId={paymentId}
              onClose={() => setPaymentId(undefined)}
              onSettled={(result) => {
                // The payment row made a new version of the invoice: open that one.
                if (result.invoiceHash && result.invoiceHash.toLowerCase() !== (hash || '').toLowerCase()) {
                  navigate(`/invoicePrint/${result.invoiceHash}?page=classic&payment=${result.publicId}`, {replace: true})
                  return
                }
                invoiceQuery.refetch()
                modelQuery.refetch()
              }}
            />
          </div>
        )}
        <InvoicePrintModalPrint
          invoice={invoice}
          model={model}
          fullPrint={fullPrint}
          withSign={{withSign: effectiveWithSign, setWithSign}}
          pdfMode={Boolean(pdfRenderToken)}
          onReadyChange={setLayoutReady}
        />
        <div className={clsx('cp-noprint', fullPrint ? 'd-none' : 'd-md-none')}>
          <InvoicePrintPOS
            invoice={invoice}
            noteLines={receiptNotes}
            title={classicTitle(model)}
            subtitle={classicSubtitle(model)}
          />
        </div>
        {!pdfRenderToken && (
          <>
            <div className='cp-noprint cp-footer-space' aria-hidden='true' />
            <div className='cp-noprint'>
              <PrintFooter
                invoice={invoice}
                fullPrint={{fullPrint, setFullPrint}}
                printReady={layoutReady}
              />
            </div>
          </>
        )}
      </div>
    )
  }

  const invoiceMissing = invoiceQuery.isSuccess && !invoice
  if (invoiceQuery.isError || invoiceMissing || modelQuery.isError) {
    const message = invoiceQuery.isError
      ? invoiceErrorMessage(invoiceQuery.error)
      : invoiceMissing
      ? 'صورتحسابی با این لینک پیدا نشد. لینک را کامل و بدون تغییر باز کنید.'
      : modelQuery.error?.message || 'دریافت صورتحساب انجام نشد. چند لحظه دیگر دوباره تلاش کنید.'
    return (
      <ErrorState
        message={message}
        onRetry={() => {
          if (invoiceQuery.isError || invoiceMissing) invoiceQuery.refetch()
          if (modelQuery.isError) modelQuery.refetch()
        }}
      />
    )
  }

  if (enabledQuery) {
    return <AppLoading mode='page' message='در حال دریافت صورتحساب…' detail='چند لحظه صبر کنید' />
  }
  return null
}

export {InvoicePrintModalPrintWrapper}
